import React, { useState } from 'react';

import Body from '../../atoms/Body';
import SVGIcon from '../../atoms/SVGIcon';
import { formatFileSize, truncate } from './utils';
import ProgressBar from '../../atoms/ProgressBar';
import ThemeV2 from '../../../componentsV2/theme';
import { StyledButton } from '../../../Utils/styles/button';
import { ButtonSize } from '../../atoms/IconButton_V2/interface';

import {
  File,
  FileType,
  FileCardProps,
  FileUploadStatus,
  FileCardWrapperProps,
} from './types';

import {
  StyledImage,
  StyledFileCard,
  StyledFileCardMain,
  StyledTextWithIcon,
  StyledBodyFileName,
  StyledFileCardHeader,
  StyledImageContainer,
  StyledFileCardFooter,
  StyledLoaderContainer,
  StyledFileCardFileSize,
  StyledImageDownloadIcon,
  StyledFileCardCloseIcon,
  StyledFileCardIconContainer,
  StyledClickableImageCloseIcon,
} from './styles';

const FileCardWrapper = (props: FileCardWrapperProps) => {
  const { children, onMouseEnter, onMouseLeave, onClick, isParticipation } =
    props;
  return (
    <StyledFileCard
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      isParticipation={isParticipation}
      data-testid="file-card-wrapper"
    >
      <StyledFileCardMain>{children}</StyledFileCardMain>
    </StyledFileCard>
  );
};

const closeButtonProps = {
  icon: 'close-rounded',
  size: ButtonSize.Small,
  iconColor: ThemeV2.palette.gray8,
  dataTestId: 'close-button',
};

const FileCard = (props: FileCardProps) => {
  const {
    file,
    status,
    className,
    errorMessage,
    onClickClose,
    onClickRetry,
    onClickDownload,
    onClickPreview,
    loadingProgress = 0,
    isParticipation = true,
  } = props;

  const [isHovered, setIsHovered] = useState(false);

  const openPreviewModal = (fileToPreview: File) => {
    if (onClickPreview && fileToPreview) {
      onClickPreview(fileToPreview);
    }
  };

  switch (status) {
    case FileUploadStatus.Loading:
      if (file && onClickClose) {
        return (
          <FileCardWrapper isParticipation={isParticipation}>
            <StyledFileCardHeader>
              <StyledFileCardCloseIcon
                onClick={() => onClickClose(file.name)}
                {...closeButtonProps}
              />
            </StyledFileCardHeader>
            <StyledLoaderContainer>
              <ProgressBar
                height="8px"
                color="flowProgress"
                value={loadingProgress}
              />
            </StyledLoaderContainer>
          </FileCardWrapper>
        );
      }
      return null;

    case FileUploadStatus.Success:
      if (file?.type === FileType.Image) {
        return (
          <StyledImageContainer
            className={className}
            onClick={() => openPreviewModal(file)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            isParticipation={isParticipation}
            data-testid="file-card-wrapper"
          >
            {file.thumbnails ? (
              <StyledImage alt={file.name} src={file.thumbnails['360']} />
            ) : (
              <StyledImage alt={file.name} src={file.url} />
            )}
            {isParticipation && onClickClose && (
              <StyledClickableImageCloseIcon
                onClick={() => onClickClose(file.name)}
              >
                <SVGIcon icon="close-circle-outlined" size="24px" />
              </StyledClickableImageCloseIcon>
            )}
            {!isParticipation && onClickDownload && isHovered && (
              <StyledImageDownloadIcon
                icon="download"
                size={ButtonSize.Small}
                iconColor={ThemeV2.palette.gray8}
                dataTestId="download-button"
                onClick={(event) => {
                  event.stopPropagation();
                  onClickDownload(file);
                }}
              />
            )}
          </StyledImageContainer>
        );
      }

      if (file?.type === FileType.File) {
        const maxFileNameLen = 20;
        return (
          <FileCardWrapper
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => openPreviewModal(file)}
            isParticipation={isParticipation}
          >
            <StyledFileCardHeader>
              {isParticipation && (
                <>
                  <StyledFileCardFileSize variant="body3" color="gray8">
                    {file?.size ? formatFileSize(file?.size) : null}
                  </StyledFileCardFileSize>
                  <StyledFileCardCloseIcon
                    onClick={() => onClickClose && onClickClose(file)}
                    {...closeButtonProps}
                  />
                </>
              )}
              <div style={{ minHeight: '28px', width: '100%' }}>
                {!isParticipation && isHovered && (
                  <StyledFileCardCloseIcon
                    icon="download"
                    size={ButtonSize.Small}
                    iconColor={ThemeV2.palette.gray8}
                    dataTestId="download-button"
                    onClick={(event) => {
                      event.stopPropagation();
                      if (onClickDownload) {
                        onClickDownload(file);
                      }
                    }}
                  />
                )}
              </div>
            </StyledFileCardHeader>
            <StyledFileCardIconContainer>
              <SVGIcon size="48px" icon="generic-file" />
            </StyledFileCardIconContainer>
            <StyledFileCardFooter>
              <StyledBodyFileName variant="body3" color="geekBlue9">
                {truncate(file?.name, maxFileNameLen)}
              </StyledBodyFileName>
            </StyledFileCardFooter>
          </FileCardWrapper>
        );
      }
      return null;

    case FileUploadStatus.Error:
      if (file) {
        return (
          <FileCardWrapper isParticipation={isParticipation}>
            <StyledFileCardHeader>
              {onClickClose && (
                <StyledFileCardCloseIcon
                  onClick={() => onClickClose(file.name)}
                  {...closeButtonProps}
                />
              )}
            </StyledFileCardHeader>
            <StyledFileCardIconContainer>
              <SVGIcon
                size="48px"
                icon="warning-hollow"
                color={ThemeV2.palette.dustRed6}
              />
            </StyledFileCardIconContainer>
            <StyledFileCardFooter>
              <Body variant="body3Medium" color="gray9">
                {errorMessage}
              </Body>
              {onClickRetry && (
                <StyledButton onClick={() => onClickRetry(file.name)}>
                  <StyledTextWithIcon
                    color="dustRed6"
                    icon="retry"
                    iconSize="12px"
                    iconColor={ThemeV2.palette.dustRed6}
                  >
                    Try again
                  </StyledTextWithIcon>
                </StyledButton>
              )}
            </StyledFileCardFooter>
          </FileCardWrapper>
        );
      }
      return null;

    default:
      return null;
  }
};

export default FileCard;
